import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api, type ContainerOut } from "@/lib/api";
import { formatDate, formatDecimal, formatPrice } from "@/lib/format";

/**
 * Lista dei container. Con uno solo e' inutile, con cinque e' la pagina.
 *
 * La colonna "Da guardare" e' l'unica ragione per aprirla: se e' vuota su
 * tutte le righe, oggi non c'e' niente da fare.
 */

const STATO: Record<ContainerOut["status"], { label: string; tone: string }> = {
  in_arrivo: { label: "in arrivo", tone: "info" },
  a_terra: { label: "a terra", tone: "ok" },
  chiuso: { label: "chiuso", tone: "muted" },
};

// Ogni segnale dice cosa NON va e cosa farci: un'etichetta che nomina solo il
// sintomo costringe a ricordarsi il resto.
const ATTENZIONE: Record<string, string> = {
  sbarco_passato: "Sbarco passato: dichiaralo a terra",
  prezzo_mancante: "Righe senza prezzo deciso",
  fermo_da_troppo: "Fermo da oltre 6 mesi",
  nessuna_riga: "Nessuna riga",
};

function Pill({ tone, children }: { tone: string; children: React.ReactNode }): JSX.Element {
  const classe: Record<string, string> = {
    ok: "pill-ok",
    info: "pill-neutral border-info/40 text-info",
    warn: "pill-risk",
    muted: "pill-neutral",
  };
  return <span className={`pill ${classe[tone] ?? classe.muted}`}>{children}</span>;
}

export function Containers(): JSX.Element {
  const qc = useQueryClient();
  const containers = useQuery({ queryKey: ["containers"], queryFn: api.containers });
  const [nuovo, setNuovo] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  const crea = useMutation({
    mutationFn: () => api.createContainer({ code: code.trim(), name: name.trim() }),
    onSuccess: () => {
      setNuovo(false);
      setCode("");
      setName("");
      setErrore(null);
      qc.invalidateQueries({ queryKey: ["containers"] });
    },
    onError: (e: Error) => setErrore(e.message),
  });

  const rows = containers.data ?? [];

  return (
    <div className="flex flex-col gap-5 px-7 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="page-title">Container</h1>
          <p className="page-subtitle">
            Merce comprata in container: costo suo, prezzo deciso a mano.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setNuovo((v) => !v)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
          Nuovo container
        </button>
      </header>

      {nuovo ? (
        <form
          className="flex flex-wrap items-end gap-3 rounded-[var(--radius-lg)] border border-line bg-card p-4"
          onSubmit={(e) => {
            e.preventDefault();
            crea.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-micro font-semibold text-muted-foreground">
              Codice
            </span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              placeholder="scarpiere-40hc"
              className="h-9 rounded-[var(--radius)] border border-line-2 bg-card px-2.5 font-mono text-data"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-micro font-semibold text-muted-foreground">
              Nome
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Scarpiere + tavoli 40'HC"
              className="h-9 w-64 rounded-[var(--radius)] border border-line-2 bg-card px-2.5 text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={crea.isPending}
            className="btn btn-primary"
          >
            {crea.isPending ? "Creo…" : "Crea"}
          </button>
          {errore ? (
            <p role="alert" className="text-sm text-danger">
              {errore}
            </p>
          ) : null}
        </form>
      ) : null}

      {containers.isLoading ? <p className="text-sm text-muted-foreground">Carico…</p> : null}

      {!containers.isLoading && rows.length === 0 ? (
        <div className="rounded-[var(--radius-lg)] border border-line bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Nessun container. Creane uno per registrare la merce comprata in container e
            togliere i suoi articoli dall&apos;auto-prezzo.
          </p>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-line bg-card">
          <table className="w-full text-data">
            <caption className="sr-only">Container registrati</caption>
            <thead>
              <tr className="border-b border-border bg-paper-2 text-left text-micro font-semibold text-muted-foreground">
                <th scope="col" className="px-3 py-2.5">Container</th>
                <th scope="col" className="px-3 py-2.5">Stato</th>
                <th scope="col" className="px-3 py-2.5">Sbarco</th>
                <th scope="col" className="px-3 py-2.5 text-right">Mesi</th>
                <th scope="col" className="px-3 py-2.5 text-right">Sku</th>
                <th scope="col" className="px-3 py-2.5 text-right">Pezzi</th>
                <th scope="col" className="px-3 py-2.5 text-right">Merce</th>
                <th scope="col" className="px-3 py-2.5 text-right">Logistica</th>
                <th scope="col" className="px-3 py-2.5">Da guardare</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const stato = STATO[c.status];
                return (
                  <tr key={c.code} className="border-b border-line transition-colors last:border-0 hover:bg-paper-2">
                    <td className="px-3 py-2.5">
                      <Link
                        to={`/container/${encodeURIComponent(c.code)}`}
                        className="font-medium underline-offset-2 hover:underline"
                      >
                        {c.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5">
                      <Pill tone={stato.tone}>{stato.label}</Pill>
                    </td>
                    <td className="px-3 py-2.5 font-mono tabular-nums text-muted-foreground">
                      {formatDate(c.landed_on)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                      {formatDecimal(c.months_in_stock)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">{c.line_count}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                      {c.total_pieces}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                      {formatPrice(c.goods_value)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                      {formatPrice(c.logistics_accrued)}
                    </td>
                    <td className="px-3 py-2.5">
                      {c.attention.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {c.attention.map((a) => (
                            <Pill key={a} tone="warn">
                              {ATTENZIONE[a] ?? a}
                            </Pill>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
